import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Prompt } from 'react-router-dom';
import { Typography, Box, Button, Backdrop, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { formatDate } from '../../../utils/formatDate';
import { usePost } from '../../../sharedModules/hooks/useQuery';
import { defaultHvacSystem } from '../components/HvacSystemFields';
import type { HvacSystemValues } from '../components/HvacSystemFields';
import { defaultPlumbingSystem } from '../components/PlumbingSystemFields';
import type { PlumbingSystemValues } from '../components/PlumbingSystemFields';
import PreviousInspectionsTable from '../components/PreviousInspectionsTable';
import PreviousInspectionModal from '../components/PreviousInspectionModal';
import { mapApiResponseToFormValues } from '../utils/mapApiResponseToFormValues';
import type { ApiInspectionReport } from '../utils/mapApiResponseToFormValues';
import { clearDraft } from '../utils/inspectionReportDraftStorage';
import { initialFormValues, type InspectionFormValues } from '../utils/inspectionReportFormDefaults';
import { buildInspectionReportFormData } from '../utils/buildInspectionReportFormData';
import {
  getAxiosErrorMessage,
  getInspectionReportMutationDisplayError,
  INSPECTION_REPORT_SUBMIT_ERROR_FALLBACK,
} from '../utils/getAxiosErrorMessage';
import { useHomeServiceManufacturers } from '../hooks/useHomeServiceManufacturers';
import { useInspectionReportDraft } from '../hooks/useInspectionReportDraft';
import { useSyncSystemPhotoSlots } from '../hooks/useSyncSystemPhotoSlots';
import InspectionReportFormAlerts from '../components/inspectionReportForm/InspectionReportFormAlerts';
import CustomerInformationSection from '../components/inspectionReportForm/CustomerInformationSection';
import HvacInspectionSection from '../components/inspectionReportForm/HvacInspectionSection';
import IndoorAirQualitySection from '../components/inspectionReportForm/IndoorAirQualitySection';
import PlumbingInspectionSection from '../components/inspectionReportForm/PlumbingInspectionSection';
import ElectricalInspectionSection from '../components/inspectionReportForm/ElectricalInspectionSection';
import WaterTreatmentInspectionSection from '../components/inspectionReportForm/WaterTreatmentInspectionSection';

export { initialFormValues, type InspectionFormValues } from '../utils/inspectionReportFormDefaults';

interface InspectionReportFormPageProps {
  homeInspectionJobId?: string;
  prefillData?: Partial<InspectionFormValues>;
  headerDisabled?: boolean;
  title?: string;
  onSubmitSuccess?: () => void;
}

const InspectionReportFormPage: React.FC<InspectionReportFormPageProps> = ({
  homeInspectionJobId,
  prefillData,
  headerDisabled = false,
  title = 'Home Services Inspection Report',
  onSubmitSuccess,
}) => {
  const prefillSnapshot = useMemo(() => JSON.stringify(prefillData ?? {}), [prefillData]);

  const [formValues, setFormValues] = useState(initialFormValues);
  const [hvacSystems, setHvacSystems] = useState<HvacSystemValues[]>([{ ...defaultHvacSystem }]);
  const [plumbingSystems, setPlumbingSystems] = useState<PlumbingSystemValues[]>([{ ...defaultPlumbingSystem }]);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hvacSystemPhotos, setHvacSystemPhotos] = useState<File[][]>([[]]);
  const [plumbingSystemPhotos, setPlumbingSystemPhotos] = useState<File[][]>([[]]);
  const [electricalPhotos, setElectricalPhotos] = useState<File[]>([]);
  const [waterTreatmentPhotos, setWaterTreatmentPhotos] = useState<File[]>([]);
  const [airQualityPhotos, setAirQualityPhotos] = useState<File[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInspectionId, setSelectedInspectionId] = useState<number | null>(null);
  const [selectedInspectionCanPrefill, setSelectedInspectionCanPrefill] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [restoredFromDraft, setRestoredFromDraft] = useState(false);

  const {
    hvacManufacturers,
    plumbingManufacturers,
    electricalManufacturers,
    waterTreatmentManufacturers,
    manufacturersError,
  } = useHomeServiceManufacturers();

  const { mutateAsync, isPending, reset, error } = usePost({
    onError: (err) => {
      setSubmitError(getAxiosErrorMessage(err, INSPECTION_REPORT_SUBMIT_ERROR_FALLBACK));
    },
  });

  useInspectionReportDraft({
    homeInspectionJobId,
    prefillSnapshot,
    formValues,
    hvacSystems,
    plumbingSystems,
    dirty,
    setFormValues,
    setHvacSystems,
    setPlumbingSystems,
    setDirty,
    setRestoredFromDraft,
    isPending,
  });

  useSyncSystemPhotoSlots(hvacSystems.length, setHvacSystemPhotos);
  useSyncSystemPhotoSlots(plumbingSystems.length, setPlumbingSystemPhotos);

  const updateField = (field: string, value: unknown) => {
    if (homeInspectionJobId) setDirty(true);
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const setHvacSystemsWithDirty = useCallback(
    (updater: React.SetStateAction<HvacSystemValues[]>) => {
      if (homeInspectionJobId) setDirty(true);
      setHvacSystems(updater);
    },
    [homeInspectionJobId],
  );

  const setPlumbingSystemsWithDirty = useCallback(
    (updater: React.SetStateAction<PlumbingSystemValues[]>) => {
      if (homeInspectionJobId) setDirty(true);
      setPlumbingSystems(updater);
    },
    [homeInspectionJobId],
  );

  const handleOpenPreviousInspection = (inspectionId: number, canPrefill: boolean) => {
    setSelectedInspectionId(inspectionId);
    setSelectedInspectionCanPrefill(canPrefill);
    setModalOpen(true);
  };

  const handlePrefill = (data: ApiInspectionReport) => {
    if (homeInspectionJobId) setDirty(true);
    const {
      formValues: mapped,
      hvacSystems: mappedHvac,
      plumbingSystems: mappedPlumbing,
    } = mapApiResponseToFormValues(data);
    setFormValues((prev) => ({ ...prev, ...mapped }));
    setHvacSystems(mappedHvac.length > 0 ? mappedHvac : [{ ...defaultHvacSystem }]);
    setPlumbingSystems(mappedPlumbing.length > 0 ? mappedPlumbing : [{ ...defaultPlumbingSystem }]);
  };

  const updateSystemPhotos = (type: 'hvac' | 'plumbing', index: number, files: File[]) => {
    if (homeInspectionJobId) setDirty(true);
    const setter = type === 'hvac' ? setHvacSystemPhotos : setPlumbingSystemPhotos;
    setter((prev) => {
      const updated = [...prev];
      updated[index] = files;
      return updated;
    });
  };

  const errorMessage =
    submitError ?? (error ? getInspectionReportMutationDisplayError(error) : null);

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!homeInspectionJobId) return;

    const submittedJobId = homeInspectionJobId;

    try {
      const formData = buildInspectionReportFormData({
        homeInspectionJobId: submittedJobId,
        formValues,
        hvacSystems,
        plumbingSystems,
        hvacSystemPhotos,
        plumbingSystemPhotos,
        electricalPhotos,
        waterTreatmentPhotos,
        airQualityPhotos,
      });

      await mutateAsync({ url: '/home_services/inspection_reports', data: formData });

      clearDraft(submittedJobId);
      setDirty(false);
      setRestoredFromDraft(false);
      if (onSubmitSuccess) {
        onSubmitSuccess();
      } else {
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 3000);
      }
    } catch (err: unknown) {
      setSubmitError(getAxiosErrorMessage(err, INSPECTION_REPORT_SUBMIT_ERROR_FALLBACK));
    }
  };

  const leaveMessage =
    'You have unsaved changes. Your report has not been submitted to the server. Are you sure you want to leave?';

  const clearError = () => {
    setSubmitError(null);
    reset?.();
  };

  return (
    <>
      <Prompt when={Boolean(homeInspectionJobId) && dirty && !isPending} message={leaveMessage} />
      <Backdrop
        open={isPending}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" size={48} />
        <Typography variant="h6" color="inherit">
          Submitting report...
        </Typography>
      </Backdrop>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          {title}
        </Typography>

        <InspectionReportFormAlerts
          restoredFromDraft={restoredFromDraft}
          submitted={submitted}
          errorMessage={errorMessage}
          manufacturersError={manufacturersError}
          onClearError={clearError}
        />

        <CustomerInformationSection
          formValues={formValues}
          headerDisabled={headerDisabled}
          updateField={updateField}
          formatDate={formatDate}
        />

        <PreviousInspectionsTable customerName={formValues.customerName} onView={handleOpenPreviousInspection} />

        <HvacInspectionSection
          hvacSystems={hvacSystems}
          setHvacSystemsWithDirty={setHvacSystemsWithDirty}
          hvacManufacturers={hvacManufacturers}
          hvacSystemPhotos={hvacSystemPhotos}
          updateSystemPhotos={updateSystemPhotos}
        />

        <IndoorAirQualitySection
          formValues={formValues}
          updateField={updateField}
          airQualityPhotos={airQualityPhotos}
          onAirQualityPhotosChange={(files) => {
            if (homeInspectionJobId) setDirty(true);
            setAirQualityPhotos(files);
          }}
        />

        <PlumbingInspectionSection
          plumbingSystems={plumbingSystems}
          setPlumbingSystemsWithDirty={setPlumbingSystemsWithDirty}
          plumbingManufacturers={plumbingManufacturers}
          plumbingSystemPhotos={plumbingSystemPhotos}
          updateSystemPhotos={updateSystemPhotos}
        />

        <ElectricalInspectionSection
          formValues={formValues}
          updateField={updateField}
          electricalManufacturers={electricalManufacturers}
          electricalPhotos={electricalPhotos}
          onElectricalPhotosChange={(files) => {
            if (homeInspectionJobId) setDirty(true);
            setElectricalPhotos(files);
          }}
        />

        <WaterTreatmentInspectionSection
          formValues={formValues}
          updateField={updateField}
          waterTreatmentManufacturers={waterTreatmentManufacturers}
          waterTreatmentPhotos={waterTreatmentPhotos}
          onWaterTreatmentPhotosChange={(files) => {
            if (homeInspectionJobId) setDirty(true);
            setWaterTreatmentPhotos(files);
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 4 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={isPending || !homeInspectionJobId}
            sx={{
              bgcolor: '#3d5a80',
              '&:hover': { bgcolor: '#2c4460' },
              px: 4,
            }}
          >
            {isPending ? 'Saving...' : 'Submit Report'}
          </Button>
        </Box>
      </Box>

      <PreviousInspectionModal
        key={selectedInspectionId}
        inspectionId={selectedInspectionId}
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedInspectionCanPrefill(false);
        }}
        onPrefill={handlePrefill}
        prefillEnabled={selectedInspectionCanPrefill}
      />
    </>
  );
};

export default InspectionReportFormPage;
